import { FormEvent, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { atoms } from '@zapier/marketing-ui';
import { mediaQueries, urls } from '@zapier/identity-utils';
import { addParamsToUrl } from '@zapier/url-utils';

import { Captcha } from '../global/Captcha';
import { SocialOptionsSeparator } from '../global/SocialOptionsSeparator';
import { Invitation, useSignup } from '../../hooks';
import { Legal } from './Legal';
import { LoggedInMessage } from './LoggedInMessage';
import { SocialItems } from './SocialItems';
import { UserInformation } from './UserInformation';
import { useCurrentUser } from './useCurrentUser';
import { dispatchSignupUserInteractionEvent } from '../../avro';
import { SSOButton } from '../global/SSOButton';
import { FoundTeamModal } from '../global/FoundTeamModal';
import { DomainCaptureErrorModal } from '../global/DomainCaptureErrorModal';
import { DiscoveredDomainAccount } from '../../utils/discoverDomain';
import { FormProvider } from 'react-hook-form';
import PasswordHeader from './PasswordHeader';
import { Metadata, PrefillData, QueryParams } from '../../types';

export type SignUpFormProps = {
  /**
   * Autofocus the email field when rendering the signup form
   */
  enableEmailAutoFocus?: boolean;
  /**
   * Key used by Google reCaptcha
   */
  googleCaptchaSiteKey: string;
  /**
   * Hide non-email fields until user has focused on form
   */
  hideFieldsUntilFocus?: boolean;
  /**
   * Forces users to log in to email account associated with invitation
   */
  invitation?: Invitation | null;
  /**
   * Url of page to redirect user to after successfully logging in
   */
  next?: string | undefined;
  /**
   * Current query params (so they can be preserved when routing)
   */
  queryParams?: QueryParams;
  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: (url: string) => void;

  /**
   * Optional selectedApis to be sent in signup request and subsequent redirect
   * to onboarding flows via query param
   */
  selectedApis?: string;

  /**
   * Optional from to be sent in signup request and subsequent redirect
   * to onboarding flows via query param. Used to determine where a user was
   * before they ended up on the sign-up page.
   */
  from?: string;

  /**
   * Optional query param for use_case_slug, used primarily for tracking
   */
  useCase?: string;

  /**
   * Should we render Google SSO?
   */
  googleSSOEnabled?: boolean;

  /**
   * Domain Capture data retrieved
   * on page load from email and code params.
   */
  domainCaptureData?: {
    isFromSsoReturn: boolean;
    errorModalMessage?: string | null;
    discoveredDomainAccount?: DiscoveredDomainAccount | null;
  };

  /**
   * Metadata used for eventing and tracking
   */
  metadata?: Metadata;

  /**
   * Form prefill data retrieved from query params
   */
  prefillData?: PrefillData;
};

const gridStyles = css`
  display: grid;
  gap: 20px;
`;

const headerStyles = css`
  margin-bottom: 16px;
  text-align: center;

  ${mediaQueries.tablet} {
    text-align: left;
  }
`;

/**
 * Displays form to allow the user to sign up. Supports username / password, SSO, SAML
 */
export function SignUpForm({
  enableEmailAutoFocus = false,
  googleCaptchaSiteKey,
  hideFieldsUntilFocus = false,
  invitation,
  next,
  queryParams = {},
  useCase,
  routeTo,
  selectedApis,
  googleSSOEnabled = true,
  from,
  domainCaptureData,
  metadata,
  prefillData,
}: SignUpFormProps) {
  const [ignoreCurrentUser, setIgnoreCurrentUser] = useState(false);

  const {
    formData,
    formErrors,
    formMethods,
    handleClearForm,
    handleFocus: signupHandleFocus,
    handleSubmit,
    isCaptchaRequired,
    shouldShowEmailField,
    shouldShowNameFields,
    shouldShowPasswordField,
    status,
    submittedFields,
    shouldShowGoogleSSO,
    discoveredDomainAccount,
    onClickUseDifferentEmail,
    onClickJoinTeam,
    onClickGoToHomepage,
  } = useSignup({
    discoveredDomainAccount: domainCaptureData?.discoveredDomainAccount,
    email: invitation?.email || prefillData?.email,
    firstName: prefillData?.firstName,
    from,
    googleSSOEnabled,
    hideFieldsUntilFocus,
    invitation: invitation?.code,
    isFromSsoReturn: domainCaptureData?.isFromSsoReturn,
    lastName: prefillData?.lastName,
    metadata,
    next,
    routeTo,
    selectedApis,
  });

  const signupWithSAML = invitation?.account.hasSamlSso;
  let samlSignUpUrl = '';
  if (signupWithSAML && invitation) {
    samlSignUpUrl = addParamsToUrl(invitation.ssoUrl, {
      invitation: invitation.code,
    });
  }

  const user = useCurrentUser();

  // Ref guard: refocus email only after the modal closes, not on mount (matters where email isn't autofocused).
  const { setFocus } = formMethods;
  const wasFoundTeamModalOpen = useRef(false);
  useEffect(() => {
    if (discoveredDomainAccount != null) {
      wasFoundTeamModalOpen.current = true;
      return;
    }
    if (wasFoundTeamModalOpen.current) {
      wasFoundTeamModalOpen.current = false;
      setFocus('email');
    }
  }, [discoveredDomainAccount, setFocus]);

  // Email is inert until Back re-renders the email step, so focus it from an effect, not the click.
  // An invitation locks email (disabled can't take focus), so First name gets it instead.
  const wentBackToEmailStep = useRef(false);
  const isEmailLocked = Boolean(invitation?.email);
  useEffect(() => {
    if (shouldShowEmailField && wentBackToEmailStep.current) {
      wentBackToEmailStep.current = false;
      setFocus(isEmailLocked ? 'firstName' : 'email');
    }
  }, [shouldShowEmailField, isEmailLocked, setFocus]);

  function handleBack() {
    wentBackToEmailStep.current = true;
    handleClearForm();
  }

  const handleSignupFormFocus = () => {
    signupHandleFocus();
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup_intent',
      signupType: 'direct',
      selectedApis,
    });
  };

  function handleSubmitWithEvents(e: FormEvent<HTMLFormElement>) {
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup',
      signupType: 'direct',
      selectedApis,
    });
    return handleSubmit(e);
  }

  // Warn the user if they are currently logged in before allowing
  // them to sign up
  if (user.currentUser?.email && !ignoreCurrentUser) {
    return (
      <LoggedInMessage
        currentUser={user.currentUser}
        next={next}
        onClick={() => setIgnoreCurrentUser(true)}
      />
    );
  }

  // Only show Captcha if it's required
  if (isCaptchaRequired) {
    return (
      <Captcha
        googleCaptchaSiteKey={googleCaptchaSiteKey}
        onCaptchaVerification={(captcha: string | null) => {
          formMethods.setValue('captcha', captcha || '');
        }}
      />
    );
  }

  return (
    <div css={gridStyles}>
      {discoveredDomainAccount != null && (
        <FoundTeamModal
          onClickUseDifferentEmail={onClickUseDifferentEmail}
          onClickGoToHomepage={onClickGoToHomepage}
          onClickJoinTeam={onClickJoinTeam}
          teamName={discoveredDomainAccount.name}
          teamAvatarUrl={discoveredDomainAccount.logoUrl}
          accessPolicy={discoveredDomainAccount.accessPolicy}
          teamDomain={discoveredDomainAccount.domain}
          isDomainCaptureBlocked={
            discoveredDomainAccount.isDomainCaptureBlocked
          }
        />
      )}
      {domainCaptureData?.errorModalMessage && (
        <DomainCaptureErrorModal
          onClickOk={() => {
            window.location.href = urls.SIGNUP_URL;
          }}
          errorMessage={domainCaptureData.errorModalMessage}
        />
      )}

      {shouldShowPasswordField && !signupWithSAML && (
        <PasswordHeader email={formData.email} onClick={handleBack} />
      )}

      {shouldShowEmailField && !signupWithSAML && (
        <SocialItems
          next={next}
          selectedApis={selectedApis}
          from={from}
          useCaseSlug={useCase}
          shouldShowGoogleSSO={shouldShowGoogleSSO}
          invitation={invitation?.code}
          skipOnboarding={metadata?.skipOnboarding === 'true'}
        />
      )}

      {signupWithSAML && (
        <div css={gridStyles}>
          <h2
            className={`${atoms.bodyLG} ${atoms.textNeutral800}`}
            css={headerStyles}
          >
            Sign up with your Identity Provider
          </h2>
          <SSOButton
            href={samlSignUpUrl}
            next={next}
            type="sso"
            shouldOpenInChildWindow={false}
          >
            Continue with SSO
          </SSOButton>
        </div>
      )}

      {shouldShowEmailField && !signupWithSAML && shouldShowGoogleSSO && (
        <SocialOptionsSeparator />
      )}

      {!signupWithSAML && (
        <FormProvider {...formMethods}>
          <UserInformation
            formData={formData}
            formErrors={formErrors}
            handleFocus={handleSignupFormFocus}
            handleSubmit={handleSubmitWithEvents}
            invitation={invitation?.code}
            queryParams={queryParams}
            shouldDisableEmailField={isEmailLocked}
            shouldFocusEmailField={Boolean(enableEmailAutoFocus)}
            shouldShowEmailField={shouldShowEmailField}
            shouldShowNameFields={shouldShowNameFields}
            shouldShowPasswordField={shouldShowPasswordField}
            status={status}
            submittedFields={submittedFields}
          />
        </FormProvider>
      )}

      {formErrors.error && status === 'error' && (
        <div
          className={`${atoms.bodySM} ${atoms.textError} ${atoms.textCenter}`}
        >
          {formErrors.error}
        </div>
      )}
      {signupWithSAML && <Legal />}
    </div>
  );
}
